import { GeoLocationDetails } from '@flowwai-frontend/shared/sales-crm/sales-crm-core'
import {
   addItemToLocalStorage,
   getItemFromLocalStorage,
   removeItemFromLocalStorage
} from '@flowwai-frontend/shared/utils/web'

interface SiteCacheEntry {
   key: string
   data: any
   accessedAt: number
}

const SITE_INFORMATION_STORAGE_KEY = 'tilequery-responses'
const MAX_ENTRIES = 10

export const buildCacheKey = (coords: GeoLocationDetails[]): string => {
   const sorted = [...coords].sort((a, b) => {
      if (a.latitude !== b.latitude) return a.latitude - b.latitude
      return a.longitude - b.longitude
   })
   return sorted.map(c => `${c.latitude},${c.longitude}`).join('|')
}

const readFromStorage = (): SiteCacheEntry[] => {
   try {
      const raw = getItemFromLocalStorage(SITE_INFORMATION_STORAGE_KEY)
      if (!raw) return []
      return JSON.parse(raw) as SiteCacheEntry[]
   } catch {
      removeItemFromLocalStorage(SITE_INFORMATION_STORAGE_KEY)
      return []
   }
}

export const getCachedSiteInfo = (key: string): any | null => {
   const entries = readFromStorage()
   const entry = entries.find(e => e.key === key)
   if (!entry) return null

   const updated = entries.map(e =>
      e.key === key ? { ...e, accessedAt: Date.now() } : e
   )
   try {
      addItemToLocalStorage(
         SITE_INFORMATION_STORAGE_KEY,
         JSON.stringify(updated)
      )
   } catch {
      // silently skip on write failure
   }
   return entry.data
}

export const setCachedSiteInfo = (key: string, data: any): void => {
   try {
      const entries = readFromStorage()
      const existingIndex = entries.findIndex(e => e.key === key)
      const newEntry: SiteCacheEntry = { key, data, accessedAt: Date.now() }

      if (existingIndex >= 0) {
         entries[existingIndex] = newEntry
      } else {
         entries.push(newEntry)
      }

      if (entries.length > MAX_ENTRIES) {
         entries.sort((a, b) => a.accessedAt - b.accessedAt)
         entries.shift()
      }

      addItemToLocalStorage(
         SITE_INFORMATION_STORAGE_KEY,
         JSON.stringify(entries)
      )
   } catch {
      // silently skip on write failure
   }
}

export const clearSiteInfoCache = (): void => {
   removeItemFromLocalStorage(SITE_INFORMATION_STORAGE_KEY)
}
